<template>
  <basic-page title="Linkit">
    <v-row class="fill-height">
      <v-col cols="12" lg="10" offset-lg="1" xl="8" offset-xl="2">
        <!-- Ryhmänjohtajat Section with Subcategories -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card color="primary" dark>
              <v-card-title class="ryhmanjohtajat-title white--text">
                <v-icon left color="white">mdi-account-group</v-icon>
                <span class="white--text">Ryhmänjohtajat</span>
              </v-card-title>
              <v-card-text>
                <!-- Subcategories -->
                <div
                  v-for="subcategory in ryhmanjohtajatSubcategories"
                  :key="subcategory.name"
                  class="mb-4"
                >
                  <h3 class="text-h6 mb-3 white--text">
                    <v-icon left color="white">{{ subcategory.icon }}</v-icon>
                    {{ subcategory.name }}
                  </h3>
                  <v-row>
                    <v-col
                      v-for="item in subcategory.items"
                      :key="item.id"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-btn
                        v-if="item.type === 'link'"
                        :href="item.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        block
                        outlined
                        color="white"
                        class="text-none"
                      >
                        {{ item.label }}
                        <v-icon right small>mdi-open-in-new</v-icon>
                      </v-btn>
                      <v-btn
                        v-else-if="item.type === 'file'"
                        @click="downloadFile(item)"
                        block
                        outlined
                        color="white"
                        class="text-none"
                        :loading="item.loading"
                      >
                        {{ item.label }}
                        <v-icon right small>mdi-download</v-icon>
                      </v-btn>
                      <v-card
                        v-else-if="item.type === 'info'"
                        outlined
                        class="info-card"
                      >
                        <v-card-text class="white--text">
                          <div class="font-weight-bold mb-2">
                            {{ item.label }}
                          </div>
                          <div
                            v-for="(line, index) in item.content"
                            :key="index"
                            class="text-body-2 mb-1"
                          >
                            <a
                              v-if="line.includes('+')"
                              :href="
                                'tel:' +
                                line.split(':')[1].trim().replace(/\s/g, '')
                              "
                              class="white--text text-decoration-none"
                            >
                              {{ line }}
                            </a>
                            <span v-else>{{ line }}</span>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="mb-6"></v-divider>

        <!-- Featured Link -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card
              href="https://halytyskoodit.web.app"
              target="_blank"
              rel="noopener noreferrer"
              hover
              class="featured-link-card"
              elevation="8"
            >
              <v-card-text class="pa-6">
                <v-row align="center">
                  <v-col cols="12" sm="auto" class="text-center text-sm-left">
                    <v-icon size="64" color="white">mdi-fire-truck</v-icon>
                  </v-col>
                  <v-col>
                    <div class="featured-link-title">Tehtäväluokat</div>
                    <div class="featured-link-subtitle">
                      Hälytys- ja tehtäväkoodit selityksineen - nopea
                      hakutyökalu
                    </div>
                  </v-col>
                  <v-col cols="12" sm="auto" class="text-center text-sm-right">
                    <v-icon size="48" color="white">mdi-chevron-right</v-icon>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Category Filter Chips -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-chip-group
              v-model="selectedCategory"
              active-class="primary--text"
              mandatory
            >
              <v-chip value="all">Kaikki</v-chip>
              <v-chip
                v-for="category in categories"
                :key="category"
                :value="category"
              >
                {{ category }}
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <!-- Links Grid -->
        <v-row>
          <v-col
            v-for="link in filteredLinks"
            :key="link.id"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              hover
              class="link-card"
            >
              <v-card-title class="text-h6">
                {{ link.label }}
                <v-spacer></v-spacer>
                <v-icon small>mdi-open-in-new</v-icon>
              </v-card-title>
              <v-card-subtitle class="pb-0">
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="(cat, index) in link.categories"
                    :key="index"
                    x-small
                    color="primary"
                    outlined
                    class="mr-1 mb-1"
                  >
                    {{ cat }}
                  </v-chip>
                </div>
              </v-card-subtitle>
              <v-card-text>
                <p class="text-body-2">{{ link.description }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-if="filteredLinks.length === 0">
          <v-col cols="12" class="text-center">
            <v-icon size="64" color="grey lighten-1">mdi-link-off</v-icon>
            <p class="text-h6 grey--text text--lighten-1 mt-4">
              Ei linkkejä valitussa kategoriassa
            </p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import app from "@/plugins/firebase.app";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

export default {
  name: "Linkit",
  components: {
    BasicPage,
  },
  data() {
    return {
      selectedCategory: "all",
      // Ryhmänjohtajat subcategories with links and downloadable files
      ryhmanjohtajatSubcategories: [
        {
          name: "Ilmoitukset",
          icon: "mdi-file-document-edit",
          items: [
            {
              id: "rj-1",
              type: "link",
              url: "https://pelastustoimi.fi/asiointi/lomakkeet/ilmoitus-ilmeinen-palonvaara",
              label: "Ilmoitus ilmeisestä palonvaarasta",
            },
            {
              id: "rj-2",
              type: "file",
              storagePath: "linkit/41_106_ilmoitus_lounais-suomi.docx",
              filename: "41_106_ilmoitus_lounais-suomi.docx",
              label: "41 § / 106 § Ilmoitus poliisille",
              loading: false,
            },
            {
              id: "rj-3",
              type: "file",
              storagePath: "linkit/41_106_ilmoitus_lounais-suomi_ohje.pdf",
              filename: "41_106_ilmoitus_lounais-suomi_ohje.pdf",
              label: "41 § / 106 § Ilmoitus poliisille - ohje",
              loading: false,
            },
          ],
        },
        {
          name: "Yleinen",
          icon: "mdi-book-open-variant",
          items: [
            {
              id: "rj-4",
              type: "link",
              url: "https://ptjkasikirja.fi",
              label: "PTJ Käsikirja",
            },
            {
              id: "rj-5",
              type: "link",
              url: "https://tokeva.fi/",
              label: "Tokeva - Kemikaalit",
            },
            {
              id: "rj-9",
              type: "file",
              storagePath: "linkit/NO_VPK_Tapaturma_ohje_2022.pdf",
              filename: "NO_VPK_Tapaturma_ohje_2022.pdf",
              label: "NoVPK Tapaturmaohje",
              loading: false,
            },
          ],
        },
        {
          name: "Keikka - kirjaaminen",
          icon: "mdi-file-document-edit-outline",
          items: [
            {
              id: "rj-9",
              type: "link",
              url: "https://prontonet.fi",
              label: "Prontonet",
            },
            {
              id: "rj-10",
              type: "link",
              url: "https://haka.spek.fi/",
              label: "HAKA",
            },
          ],
        },
        {
          name: "Yhteystiedot",
          icon: "mdi-phone",
          items: [
            {
              id: "rj-6",
              type: "info",
              label: "Päivystävät palomestarit (24/7)",
              content: [
                "RSAP31 (Karhukunnat): +358 44 701 1503",
                "RSAP32 (Ala-Satakunta): +358 44 701 7353",
                "RSAP33 (Pohjois-Satakunta): +358 44 701 7462",
              ],
            },
            {
              id: "rj-8",
              type: "info",
              label: "Tilannekeskus",
              content: ["Puhelinvaihde ja neuvonta: +358 26211500"],
            },
            {
              id: "rj-7",
              type: "link",
              url: "https://pelastustoimi.fi/satakunta/ota-yhteytta",
              label: "Satakunnan pelastuslaitos - Ota yhteyttä",
            },
          ],
        },
        // Add more subcategories here as needed
        // {
        //   name: "Koulutusmateriaalit",
        //   icon: "mdi-school",
        //   items: [...],
        // },
      ],
      links: [
        {
          id: 1,
          url: "https://noormarkunvpk.fi",
          label: "Noormarkun VPK:n kotisivu",
          description: "Virallinen kotisivu ja ajankohtaiset tiedot",
          categories: ["Yleinen"],
        },
        {
          id: 2,
          url: "https://www.spek.fi",
          label: "SPEK",
          description:
            "Suomen Pelastusalan Keskusjärjestö - koulutukset ja materiaalit",
          categories: ["Yleinen", "Koulutus"],
        },
        {
          id: 3,
          url: "https://www.pelastusopisto.fi",
          label: "Pelastusopisto",
          description: "Pelastusalan koulutus ja tutkimustoiminta",
          categories: ["Koulutus"],
        },
        {
          id: 4,
          url: "https://www.sppl.fi",
          label: "Suomen Palopäällystöliitto",
          description:
            "Palopäällystön ammattijärjestö - koulutukset ja asiantuntijatoiminta",
          categories: ["Yleinen", "Koulutus"],
        },
        {
          id: 5,
          url: "https://www.ilmatieteenlaitos.fi",
          label: "Ilmatieteen laitos",
          description: "Sääennusteet ja varoitukset",
          categories: ["Turvallisuus"],
        },
        {
          id: 6,
          url: "https://www.112.fi",
          label: "112 Suomi",
          description: "Hätäkeskuslaitos ja hätätilanneviestintä",
          categories: ["Turvallisuus"],
        },
        {
          id: 7,
          url: "https://www.tukes.fi",
          label: "Tukes",
          description:
            "Turvallisuus- ja kemikaalivirasto - määräykset ja ohjeet",
          categories: ["Turvallisuus"],
        },
        {
          id: 8,
          url: "https://www.spek.fi/koulutus",
          label: "SPEK Koulutuskalenteri",
          description: "Tulevat kurssit ja koulutustapahtumat",
          categories: ["Koulutus"],
        },
        {
          id: 9,
          url: "https://haka.spek.fi/JulkisetTapahtumat/tapahtumakalenteri.aspx",
          label: "HAKA Tapahtumakalenteri",
          description: "SPEK:n tapahtumakalenteri ja kurssihaku",
          categories: ["Koulutus"],
        },
        {
          id: 10,
          url: "https://www.poliisi.fi",
          label: "Poliisi.fi",
          description: "Poliisin verkkopalvelut ja turvallisuusohjeita",
          categories: ["Turvallisuus"],
        },
        {
          id: 11,
          url: "https://www.pelastustoimi.fi",
          label: "Pelastustoimi.fi",
          description: "Pelastustoimen verkkopalvelu ja tietopankki",
          categories: ["Yleinen"],
        },
        {
          id: 12,
          url: "https://www.palosuojelurahasto.fi",
          label: "Palosuojelurahasto",
          description: "Palosuojelun tutkimus ja kehitystyön rahoitus",
          categories: ["Yleinen"],
        },
        {
          id: 13,
          url: "https://www.pelastusopisto.fi/koulutuskalenteri/",
          label: "Pelastusopiston koulutuskalenteri",
          description: "Pelastusopiston kurssit ja koulutukset",
          categories: ["Koulutus"],
        },
        {
          id: 14,
          url: "https://kortit.spek.fi/",
          label: "SPEK Turvallisuuskortit",
          description: "Tulityö-, alkusammutus- ja muut turvallisuuskortit",
          categories: ["Koulutus"],
        },
        {
          id: 15,
          url: "https://moodle.smedu.fi/koulumaali",
          label: "Koulumaali Moodle",
          description: "Pelastusopiston koulutusalusta",
          categories: ["Koulutus"],
        },
      ],
    };
  },
  methods: {
    async downloadFile(item) {
      try {
        // Set loading state
        item.loading = true;

        // Get Firebase Storage reference
        const storage = getStorage(app);
        const fileRef = ref(storage, item.storagePath);

        // Get download URL
        const url = await getDownloadURL(fileRef);

        // Create temporary link and trigger download
        const link = document.createElement("a");
        link.href = url;
        link.download = item.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$toast.success(`Ladataan: ${item.filename}`);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$toast.error(
          `Virhe tiedoston lataamisessa: ${error.message || "Tuntematon virhe"}`
        );
      } finally {
        item.loading = false;
      }
    },
  },
  computed: {
    categories() {
      // Get unique categories from all links (flatten arrays)
      const allCategories = this.links.flatMap((link) => link.categories);
      const uniqueCategories = [...new Set(allCategories)];
      return uniqueCategories.sort();
    },
    filteredLinks() {
      if (this.selectedCategory === "all") {
        return this.links;
      }
      return this.links.filter((link) =>
        link.categories.includes(this.selectedCategory)
      );
    },
  },
};
</script>

<style scoped>
.ryhmanjohtajat-title {
  background-color: rgba(0, 0, 0, 0.2);
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #ffffff !important;
}

.ryhmanjohtajat-title,
.ryhmanjohtajat-title * {
  color: #ffffff !important;
}

.featured-link-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.featured-link-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4) !important;
  border-color: rgba(255, 255, 255, 0.6);
}

.featured-link-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.featured-link-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.95);
  margin-top: 8px;
  font-weight: 400;
}

.info-card {
  border-color: white !important;
  border-width: 2px;
  background-color: rgba(255, 255, 255, 0.1);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.info-card a:hover {
  text-decoration: underline !important;
}

.info-card .v-card__text {
  word-break: break-word;
}

.v-btn.text-none {
  white-space: normal;
  height: auto !important;
  min-height: 36px;
  padding: 8px 16px !important;
  line-height: 1.4;
  word-break: break-word;
}

.v-card__text .v-row {
  flex-wrap: wrap !important;
}

.link-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease-in-out;
}

.link-card:hover {
  transform: translateY(-4px);
}

.link-card .v-card__subtitle {
  overflow: hidden;
}

.v-chip {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
